import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import qs from 'qs';
import { DropMenu, DropMenuTrigger, ColumnProps, OverlayTrigger, PopOver } from '@tencent/spaui';
import { ListView as PTPListView } from '@tencent/ptp-fe-common';
import EventBus from 'eventing-bus';
import animate from '@/utils/animate';
import AccountSelect, { ACCOUNT_SELECT_CHANGE_LISTENER } from '@/components/AccountSelect';
import { getLabelList } from '@/api/operator/cgi/smb/manage/label';
import globalStore from '@/stores/index';
import { getFilterMap } from '@/business-components/common/filter';
import { reportUserBehavior } from '@/pages/agency/utils/util';
import { getAgencyUidByUid } from './api';
import { PAGE_SIZE_OPTIONS } from './constants';
import useDeepEffect from '@/common/hooks/use-deep-effect';
import useKeysEffect from './hooks/use-key-effect';
import TipIcon from '@/common-components/tip-icon';
import { stringToReactElement } from '@/utils/jsx';
import { getFromCombinedValue, saveCombinedValue } from '@/utils/storage';
import { FLAGS } from '@/constants/Constants';
import './assets/less/index.scoped.less';

export interface ListViewProps {
  /**
   * ListView的ref
   */
  listRef?: any;
  /**
   * 组件类名
   */
  className?: string;
  /**
   * 配置函数
   */
  options: any;
  /**
   * 业务变量集合
   */
  payloads?: {
    [key: string]: any;
  };
  /**
   * 业务方法集合
   */
  customFunctions?: {
    [key: string]: any;
  };
  /**
   * 展示的列
   */
  columns?: ColumnProps;
  /**
   * 默认的筛选项
   */
  defaultFilterKeys?: Array<string | {
    key: string;
    [key: string]: any;
  }>;
  /**
   * 全部的筛选项，默认和defaultFilterKeys一致
   */
  allFilterKeys?: Array<string | {
    key: string;
    [key: string]: any;
  }>;
  /**
   * 筛选项额外配置，主要用于筛选项动态配置
   */
  additionalFilterConfig?: {
    [key: string]: any;
  };
  /**
   * 使用自定义标签筛选项
   */
  useCustomLabelFilter?: boolean;
  /**
   * 使用地址栏参数
   */
  useUrlParams?: boolean;
  /**
   * 使用筛选项上报
   */
  useReportFilterChange?: boolean;
  /**
   * 忽略上报的参数
   */
  ignoreReportParams?: string[];
  /**
   * 使用自动切换服务商
   */
  useAutoChangeAgency?: boolean;
  /**
   * 服务商uid
   */
  agency_uid?: number;
  /**
   * 表格唯一键，用于区分同页面多个表格
   */
  tableKey?: string;
  /**
   * 动态filterFetch参数
   */
  filterParamsDep?: Record<string, any>;
  /**
   * 是否本地保存用户pageSize选择
   */
  pageSizeStoreKey?: string;
}

export interface FetchConfig {
  action: (params: any, cancelToken?: any) => Promise<any>;
  $onRequest?: (params: any) => any;
  $onResponse?: (data: any, result: any) => any;
  $onError?: (error: Error) => void;
  [key: string]: any;
}

export interface FilterConfig {
  [key: string]: any;
}

export interface TableConfig {
  columns: ColumnProps[];
  [key: string]: any;
}

const ListViewComponent: React.FC<ListViewProps> = (props: ListViewProps) => {
  // 是否初始化
  const [initDone, setInitDone] = useState(false);
  // 全部filter
  const [allFilter, setAllFilter] = useState<any[]>([]);
  // 展示的filterKeys
  const [activeFilterKeys, setActiveFilterKeys] = useState<string[]>([]);
  // 自定义的筛选项数据
  const [customFilterData, setCustomFilterData] = useState<any[]>([]);
  // 可切换name的filter的值
  const [nameData, setNameData] = useState<object>({});
  // 默认值
  const [defaultValuesByKey, setDefaultValuesByKey] = useState<object>({});
  // 是否动画
  const [animating, setAnimating] = useState(false);
  // 当前展示的筛选项，为空时展示全部
  const [displayKeys, setDisplayKeys] = useState<Array<string>>([]);

  const {
    listRef = React.createRef(), payloads: _payloads, customFunctions: _customFunctions, columns = [],
    className, options, defaultFilterKeys = [], useUrlParams, useAutoChangeAgency, agency_uid, allFilterKeys = [],
    additionalFilterConfig = {}, useCustomLabelFilter, useReportFilterChange = false, ignoreReportParams = [],
    tableKey, filterParamsDep, pageSizeStoreKey,
  } = props;
  const history = globalStore.history;
  const userId = globalStore.account.user_id;
  const urlParamsKey = tableKey ? `${tableKey}_params` : 'params';
  const fullPageSizeStoreKey = pageSizeStoreKey ? `TABLE_PAGE_SIZE_${pageSizeStoreKey}` : '';

  const [defaultPageSize] = useState<number>(() => {
    if (!fullPageSizeStoreKey) {
      return 10;
    }
    const storePageSize = getFromCombinedValue(FLAGS.USER_CUSTOM_CONFIG, fullPageSizeStoreKey, {
      userId,
    });
    return storePageSize || 10;
  });

  useEffect(() => {
    initFilter();
  }, []);

  /**
   * 仅处理spaui组件相关的config和defaultVaule值变更
   * defaultValue改变，会导致filter值被重置，
   * 因此不修改defaultValue配置，而是直接将listRef.current.filterData设为defaultValue
   */
  useDeepEffect(() => {
    // 修改config配置
    setAllFilter((allFilter) => {
      return allFilter.map((v) => {
        const { key } = v;
        // 没有config设置时，直接返回
        if (!additionalFilterConfig?.[key]) {
          return v;
        }

        const { defaultValue, ...rest } = additionalFilterConfig[key];
        return {
          ...v,
          ...rest,
          config: {
            ...(v.config || {}),
            ...(additionalFilterConfig[key].config || {}),
          },
        };
      });
    });

    // 修改listview选中值
    const filterData = { ...listRef?.current?.filterData || {} };
    Object.keys(additionalFilterConfig).forEach((key) => {
      if (additionalFilterConfig[key].defaultValue !== undefined) {
        filterData[key] = additionalFilterConfig[key].defaultValue;
      }
    });
    listRef?.current?.setFilterData(filterData);
  }, [additionalFilterConfig]);

  useKeysEffect((displayKeys) => {
    setDisplayKeys(displayKeys);
  }, defaultFilterKeys);

  // filter fetch参数改变，重新拉取
  useDeepEffect(() => {
    allFilter.forEach((v) => {
      if (v.params) {
        let hasChange = false;
        Object.keys(v.params).forEach((key: string) => {
          if (filterParamsDep?.[key] !== undefined && v.params[key] !== filterParamsDep[key]) {
            hasChange = true;
            v.params[key] = filterParamsDep[key];
          }
        });
        if (hasChange) {
          filterFetchData(v);
        }
      }
    });
  }, [filterParamsDep]);


  /**
   * pageSize改变时保存在本地
   */
  useEffect(() => {
    if (fullPageSizeStoreKey && listRef.current?.pageSize) {
      saveCombinedValue(FLAGS.USER_CUSTOM_CONFIG, fullPageSizeStoreKey, listRef.current?.pageSize, {
        userId,
      });
    }
  }, [fullPageSizeStoreKey, listRef.current?.pageSize]);

  /**
     * @description 初始化筛选项
     */
  const initFilter = async () => {
    const urlParams = JSON.parse(qs.parse(history.location?.search?.substring(1))?.[urlParamsKey] as string || '{}');

    const _nameData = nameData;
    const _defaultValuesByKey: any = {};
    const _allFilter = getAllFilter(_nameData);

    const _activeFilterKeys = defaultFilterKeys.map((v) => (_.isObject(v) ? v.key : v));
    const _customFilterData: any = [];

    const addDefaultFilterValue = (key: string, value: any, filterKey = '') => {
      if (value !== undefined) {
        _defaultValuesByKey[key] = value;
        if (activeFilterKeys.indexOf(filterKey || key) === -1) {
          activeFilterKeys.push(filterKey || key);
        }
      }
    };

    if (useUrlParams) {
      _allFilter.forEach((v) => {
        if (_.isObject(v.nameMap)) {
          _.keys(v.nameMap).forEach((key) => {
            addDefaultFilterValue(key, urlParams[key], v.key);
          });
        } else {
          addDefaultFilterValue(v.key, urlParams[v.name]);
        }
      });
    }

    const newAllFilter = _allFilter.map((v) => {
      // 如果地址栏包含参数，且当前筛选项支持切换name
      if (useUrlParams && _.isObject(v.nameConfig) && _.isArray(v.nameConfig.data)) {
        for (const x of v.nameConfig.data) {
          if (_.has(urlParams, x.value)) {
            _nameData[v.key] = x.value;
            _defaultValuesByKey[v.key] = _defaultValuesByKey[x.value];
            delete _defaultValuesByKey[x.value];
            return {
              ...v,
              ...(v.nameMap[x.value] || {}),
              name: x.value,
              title: x.label,
            };
          }
        }
      }
      if (!v.fixed) {
        _customFilterData.push({
          label: v.title,
          value: v.key,
        });
        v.display = true;
      }
      return v;
    });

    const customizedLabelList = useCustomLabelFilter ? await getCustomizedLabelList() : [];

    const customizedLabelFilter = customizedLabelList.map((v: any) => {
      const item = {
        key: v.value,
        name: v.value,
        title: v.label,
        type: 'select',
        config: {
          data: (v.label_value_list || '').split(','),
        },
        display: true,
      };

      if (useUrlParams) {
        const key = `customized_label-${v.label_type}`;
        addDefaultFilterValue(key, urlParams[key]);
      }

      return item;
    });

    // 运营方式
    if (useUrlParams && urlParams['customized_label-1000']) {
      addDefaultFilterValue('operation_label', urlParams['customized_label-1000'], 'customized_label-1000');
    }

    const allFilter = [...newAllFilter, ...customizedLabelFilter];

    setInitDone(true);
    setAllFilter(allFilter);
    setActiveFilterKeys(_activeFilterKeys);
    setCustomFilterData([..._customFilterData, ...customizedLabelList]);
    setNameData(_nameData);
    setDefaultValuesByKey(_defaultValuesByKey);
    setInitDone(true);

    allFilter.forEach((v) => {
      filterFetchData(v);
    });
  };

  /**
   * @description 获取全部筛选项
   */
  const getAllFilter = (nameData: any) => {
    const _allFilterKeys = _.isEmpty(allFilterKeys) ? defaultFilterKeys : allFilterKeys;
    const filterMap = getFilterMap(nameData, handleNameDataChange, props?.payloads);
    const _allFilter = _.compact(_allFilterKeys.map((v) => {
      if (_.isString(v) && _.isObject(filterMap[v])) {
        const fixed = Boolean(_.find(defaultFilterKeys, (x) => (_.isObject(x) ? x.key === v : x === v)));
        return {
          ...filterMap[v],
          key: v,
          fixed,
          ...(additionalFilterConfig[v] || {}),
          config: {
            ...(filterMap[v].config || {}),
            ...(additionalFilterConfig[v]?.config || {}),
          },
          ...(filterMap[v].tip ? {
            title: <TipIcon value={filterMap[v].title}>{filterMap[v].tip}</TipIcon>,
          } : {}),
        };
      }
      if (_.isObject(v) && _.isObject(filterMap[v.key])) {
        const fixed = Boolean(_.find(defaultFilterKeys, (x) => (_.isObject(x) ? x.key === v.key : x === v.key)) && v.fixed !== false);
        const newFilterConfig = {
          ...filterMap[v.key],
          ...v,
        };
        return {
          ...newFilterConfig,
          fixed,
          ...(additionalFilterConfig[v.key] || {}),
          config: {
            ...(filterMap[v.key].config || {}),
            ...(v.config || {}),
            ...(additionalFilterConfig[v.key]?.config || {}),
          },
          ...(newFilterConfig.tip ? {
            title: <TipIcon value={newFilterConfig.title}>{newFilterConfig.tip}</TipIcon>,
          } : {}),
        };
      }
      return false;
    }));

    return _allFilter.map((v) => {
      const item = _.find(allFilter, (x) => x.name === v.name) || {};
      if (item.config?.data) {
        return {
          ...v,
          config: {
            ...(v.config || {}),
            data: item.config.data,
          },
        };
      }
      return v;
    });
  };

  /**
   * @description 获取筛选项下拉数据
   */
  const filterFetchData = (v: any, _params = {}) => {
    const { fetchData, convertData, search, getSearchParams, clickLoad, getClickLoadParams } = v.config || {};
    if (fetchData) {
      const params = {
        ...(_.isObject(v.params) ? v.params : {}),
        ..._params,
      };
      fetchData(params).then((res: any) => {
        const data = _.isFunction(convertData) ? convertData(res, params, v, clickLoad) : res;
        // 初始化时返回数据
        if (_.isFunction(v.callback) && _.isEmpty(_params)) {
          v.callback(res, data);
        }
        setAllFilter((allFilter) => {
          const _allFilter = allFilter.map((x) => {
            if (x.key === v.key) {
              const item = {
                // 动态获取默认值
                ...(_.isFunction(x.getDefaultValue) ? {
                  defaultValue: x.getDefaultValue(data),
                } : {}),
                ...x,
                config: {
                  ...(x.config || {}),
                  data,
                },
              };
              // 获取筛选项下拉搜索
              if (search && _.isFunction(getSearchParams)) {
                item.config = {
                  ...item.config,
                  onSearch: _.debounce((event, value) => filterFetchData(item, getSearchParams(value)), 500),
                };
              }
              // 筛选项点击加载
              if (clickLoad && _.isFunction(getClickLoadParams)) {
                item.config = {
                  ...item.config,
                  onNodeClick: (event: any, value: any, node: any) => {
                    const params = getClickLoadParams(node);
                    if (params) {
                      filterFetchData(item, params);
                    }
                  },
                };
              }
              return item;
            }
            return x;
          });
          return _allFilter;
        });
      });
    }
  };

  /**
   * @description 参数加工
   * @param  {} params
   * @param  {} controls
   */
  const getParams = (_params: any, controls: any) => {
    let params: any = {};
    const customized_label_list: any[] = [];
    _.keys(_params).forEach((key) => {
      const value = _params[key];
      const item = _.find(controls, (v) => v.name === key) || {};
      // 空值不传
      if (!_.isNumber(value) && !_.isBoolean(value) && (!value || _.isEmpty(value))) {
        return;
      }
      // 自定义参数转换
      if (_.isFunction(item.convertValue)) {
        const itemParams = item.convertValue(value, item);
        if (itemParams && _.isObject(itemParams)) {
          params = {
            ...params,
            ...itemParams,
          };
        }
        return;
      }
      // 自定义标签
      if (_.includes(key, 'customized_label')) {
        const [__, label_type] = key.split('-');
        customized_label_list.push({
          label_type,
          label_values: [value],
        });
        return;
      }
      // 映射成多个字段，如日期
      if (_.isObject(value) && _.isObject(item.nameMap)) {
        _.keys(value).forEach((x) => {
          const k = item.nameMap[x] || x;
          if (!_.isNumber(value[x]) && !value[x]) {
            return;
          }
          params[k] = value[x];
        });
        return;
      }
      params[key] = value;
    });
    if (customized_label_list.length > 0) {
      params.customized_label_list = customized_label_list;
    }
    return params;
  };

  /**
   * @description 结果加工
   * @param  {} data
   */
  const getResult = (data: any) => {
    const result = {
      list: (data?.data?.list || []).map((v: any, index: number) => {
        const { customized_label_list = [] } = v;
        const item = {
          ...v,
          ...(index === 0 ? {
            // 兼容两种层级error_fields
            _error_fields: ([
              ...data?.error_fields || [],
              ...data?.data?.error_fields || [],
            ]).reduce((map: Record<string, any>, item: any) => ({ ...map, [item.error_field]: item }), {}),
          } : {}),
        };
        customized_label_list.forEach((x: any) => {
          const { label_type, label_value } = x;
          item[label_type] = label_value;
        });
        return item;
      }),
      page_info: data.data.page_info || {
        total_number: data.data.conf.total_num,
      },
    };
    return result;
  };

  /**
   * @description 更新请求参数从地址栏参数
   * @param  {} requestParams
   */
  const updateUrlParams = (_params: any) => {
    const params = _.pickBy(_params, (v, k) => ['page', 'page_size', 'order', ...ignoreReportParams].indexOf(k) === -1);
    const search = qs.parse(history.location?.search?.substring(1)) || {};
    const urlParams = JSON.parse(search[urlParamsKey] as string || '{}');
    if (!_.isEqual(params, urlParams)) {
      search[urlParamsKey] = JSON.stringify(params);
      history.replace({
        pathname: history.location.pathname,
        search: `?${qs.stringify(search, { encode: false })}`,
      });
    }
  };

  /**
   * @description 筛选项上报
   * @param  {} requestParams
   */
  const reportFilterChange = (requestParams: any) => {
    if (!useReportFilterChange) {
      return;
    }
    const { data: _params = {} } = requestParams || {};
    const params = _.pickBy(_params, (v, k) => ['page', 'page_size', ...ignoreReportParams].indexOf(k) === -1);
    _.keys(params).forEach((key) => {
      // 自定义标签上报
      if (key === 'customized_label_list') {
        const list = params[key];
        if (_.isArray(list)) {
          list.forEach((v) => {
            const item = _.find(allFilter, (x) => x.name === `customized_label-${v.label_type}`) || {};
            const relation_id = _.isArray(v.label_values) ? v.label_values.join(',') : v.label_values;
            const relation_type = item.title || v.label_type || '';
            reportUserBehavior({
              behaviorType: '查询',
              relation_id,
              relation_type,
            });
          });
        }
        return;
      }
      // 非自定义标签上报
      const item = _.find(allFilter, (v) => v.name === key) || {};
      const relation_id = params[key];
      const relation_name = item.type === 'select' ? (_.find(item.config?.data || [], (v) => v.value === relation_id) || {}).label || relation_id : relation_id;
      const relation_type = item.title || key;
      reportUserBehavior({
        behaviorType: '查询',
        relation_id,
        relation_type,
        relation_name,
      });
    });
  };

  /**
     * @description 处理name下拉选中变更
     */
  const handleNameDataChange = async (e: any, value: string, key: string, nameMap: any) => {
    const _nameData = nameData;
    _nameData[key] = value;
    setNameData(_nameData);
    const _allFilter = getAllFilter(_nameData);
    setAllFilter(_allFilter);
    const item = {
      ...(nameMap[value] || {}),
      key,
    };
    filterFetchData(item);
  };

  /**
     * @description 获取自定义标签
     */
  const getCustomizedLabelList = async () => {
    const { code, data = {} } = await getLabelList();
    if (code === 0) {
      const { list = [] } = data;
      const customizedLabelList = list.map((v: any) => ({
        ...v,
        value: `customized_label-${v.label_type}`,
        label: v.label_type_name,
      }));
      return customizedLabelList;
    }
    return [];
  };

  /**
     * @description 处理自定义筛选项选中
     */
  const handleCustomFilterDataChange = (event: any, value: any) => {
    setActiveFilterKeys(value.custom);
  };

  /**
     * @description 自动切换服务商
     */
  const changeAgency = (agency_uid: number, uid: number) => {
    const agency = _.find(AccountSelect.getAgencyList(), (v) => v.agency_uid === agency_uid);
    if (agency) {
      EventBus.publish(ACCOUNT_SELECT_CHANGE_LISTENER, agency_uid);
    }

    if (animating) {
      return;
    }
    const item = $('.ptp-listview .filter-list .filter-item:first-child .filter-item-val');
    if (item.children('#uid-animate').length === 0) {
      item.append('<div id="uid-animate"></div>');
    }
    $('#uid-animate').html(uid);
    const sourceElement = document.querySelector('#uid-animate');
    const targetElement = document.querySelector('#global-account-select');
    animate({
      sourceElement,
      targetElement,
      show: true,
      display: 'flex',
      duration: 2000,
      easing: 'cubic-bezier(0.42, 0, 0.58, 1)',
      delay: 100,
      onFinish: () => {
        setAnimating(false);
      },
      onError: () => {
        setAnimating(false);
      },
    } as any);
    setAnimating(true);

    // 延迟检索，让agency_uid更新到页面的list或其他子组件（测试延时0ms即可）
    return new Promise((res) => {
      setTimeout(res, 0);
    });
  };

  /**
     * @description 渲染不可用字段
     */
  const renderErrorMessage = (content: any, type: string, name: string, data: any) => {
    if (type === 'data' && data._error_fields?.[name]) {
      return <TipIcon value={content}>当前列数据不可用，请稍后重试 <br />{data._error_fields[name].error_msg || ''}</TipIcon>;
    }
    return content;
  };

  const payloads = {
    ..._payloads,
    listRef,
    allFilter,
    activeFilterKeys,
    customFilterData,
    defaultValuesByKey,
  };

  const customFunctions = {
    ..._customFunctions,
    _getParams: getParams,
    _getResult: getResult,
    _updateUrlParams: updateUrlParams,
    _reportFilterChange: reportFilterChange,
    _handleCustomFilterDataChange: handleCustomFilterDataChange,
    _changeAgency: changeAgency,
    _renderErrorMessage: renderErrorMessage,
  };

  /**
   * @description 获取最新配置
   */
  const getOptions = useCallback(({ payloads, customFunctions }: {
    payloads?: any;
    customFunctions?: any;
  }) => {
    const { fetchConfig, filterConfig, tableConfig }: {
      fetchConfig: FetchConfig;
      filterConfig: FilterConfig;
      tableConfig: TableConfig;
    } = options({ payloads, customFunctions });
    const { listRef, allFilter, activeFilterKeys, customFilterData, defaultValuesByKey } = payloads;
    const { _getParams, _getResult, _updateUrlParams, _reportFilterChange, _handleCustomFilterDataChange, _changeAgency, _renderErrorMessage } = customFunctions;
    const action = async (params: any) => {
      const uid_list = params.data?.uid_list || [];
      // 自动切换服务商
      if (useAutoChangeAgency && _.isArray(uid_list) && uid_list.length === 1) {
        const { code, data: { agency_uid: _agency_uid } } = await getAgencyUidByUid(uid_list[0]);
        if (code === 0 && _agency_uid && agency_uid !== _agency_uid) {
          await _changeAgency(_agency_uid, uid_list[0]);
        }
      }
      return fetchConfig.action(params);
    };

    const defaultOrderBy = tableConfig?.columns?.filter((v) => !v.hide)?.find?.((v) => v.defaultOrderBy)?.name;

    return {
      fetchConfig: {
        ...fetchConfig,
        action,
        $onRequest: (_params: any) => {
          const params = fetchConfig.$onRequest ? fetchConfig.$onRequest(_params) : _params;
          const requestParams = { data: _getParams(params, allFilter) };

          if (!listRef || !_.isEqual(requestParams, listRef?.current?.requestParams)) {
            setTimeout(() => {
              if (useUrlParams) {
                _updateUrlParams(_params);
              }
              _reportFilterChange(requestParams);
              if (_.isFunction(fetchConfig.beforeRequest)) {
                fetchConfig.beforeRequest(requestParams);
              }
            }, 0);
          }
          return requestParams;
        },
        $onResponse: (data: any) => {
          listRef?.current?.setSelectKeys([]);
          tableConfig?.onOptionalChange?.(new Event('OPTIONAL_CHANGE'), []);
          if (data.code === 0) {
            const _result = _getResult(data);
            const result = fetchConfig.$onResponse ? fetchConfig.$onResponse(data, _result) || _result : _result;
            return result;
          }
          return {
            list: [],
            page_info: {},
          };
        },
      },
      ...(_.isObject(filterConfig) || defaultFilterKeys.length > 0
        ? {
          filterConfig: {
            resetable: true,
            ...filterConfig,
            defaultValuesByKey,
            controls: allFilter.filter((v: any) => (!displayKeys.length || displayKeys.includes(v.key) || v.display)
              && !v.hide),
            // 自定义筛选项
            ...(customFilterData.length > 0
              ? {
                customConfigure: {
                  loading: false,
                  customFilterComponent: (
                    <DropMenuTrigger
                      className="ptp-custom-column"
                      closeAfterSelect={true}
                      dropMenu={
                        <DropMenu
                          className="with-checkmark"
                          multiple={true}
                          data={[
                            {
                              name: 'custom',
                              list: customFilterData,
                            },
                          ]}
                          value={{
                            custom: activeFilterKeys,
                          }}
                          onChange={_handleCustomFilterDataChange}
                        />
                      }
                    >
                      <div className="button-filter-add">
                        <i className="spaui-icon icon-plus"></i>
                        <span className="spaui-text">添加筛选项</span>
                      </div>
                    </DropMenuTrigger>
                  ),
                  activeFilterKeys,
                },
              }
              : {}),
          },
        }
        : {}),
      tableConfig: {
        defaultOrderBy,
        defaultPageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        hideErrorMessage: true,
        ...tableConfig,
        ...(_.isArray(tableConfig.columns)
          ? {
            columns: tableConfig.columns.filter((v: any) => !v.hide).map((v: any) => {
              const customRender = (type: string, name: string, value: any, data: any, checkedValue: any, disabled: any) => {
                const content = _.isFunction(v.customRender) ? v.customRender(type, name, value, data, checkedValue, disabled, v) : value;
                // 空值处理
                if (!_.isNumber(content) && (!content || _.isEmpty(content))) {
                  return _renderErrorMessage('-', type, name, data);
                }

                // 有配置zh_cn时，中文映射
                if (v.zhCn && v.zhCn[content]) {
                  return _renderErrorMessage(v.zhCn[content], type, name, data);
                }

                if (type === 'data' && v.usePopover) {
                  return _renderErrorMessage(<OverlayTrigger
                    overlay={<PopOver>{content}</PopOver>}
                  >
                    <div className="inner-tips" style={{
                      ...(!!v.maxWidth && { maxWidth: v.maxWidth }),
                    }}>{content}</div>
                  </OverlayTrigger>, type, name, data);
                }
                // 有tip配置且没有自定义render时，渲染表头tip
                if (type === 'head' && v.tip && value === content) {
                  return <TipIcon value={content}>{stringToReactElement(v.tip)}</TipIcon>;
                }
                return _renderErrorMessage(content, type, name, data);
              };
              return {
                ...v,
                title: v.title || v.label,
                maxWidth: v.maxWidth || 500, // list-view样式默认单元格最宽500
                customRender,
              };
            }),
          }
          : {}),
      },
    };
  }, [payloads, customFunctions, defaultPageSize]);

  return <div className="base-list-view">{columns.length > 0 && initDone ? <PTPListView ref={listRef} options={getOptions} payloads={payloads} customFunctions={customFunctions} className={className} /> : null}</div>;
};

ListViewComponent.defaultProps = {
  payloads: {},
  customFunctions: {},
  columns: [],
  defaultFilterKeys: [],
  allFilterKeys: [],
  additionalFilterConfig: {},
  useCustomLabelFilter: false,
  useUrlParams: true,
  useReportFilterChange: false,
  ignoreReportParams: [],
  useAutoChangeAgency: false,
};

export const ListView = ListViewComponent;

export default ListView;
